import { Box, Divider, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { format_Number } from "../../utils/utils";
import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CombinationCard = ({
  cardCss,
  data,
  selectedDate,
  reservType,
  adult,
  child,
  kid,
  baby,
  toddler,
  languageWords,
  totalCount,
  combinationUpsale,
}) => {
  const theme = useTheme();
  dayjs.extend(advancedFormat);
  const { combinationQTY, selectedCombination } = useSelector(
    (store) => store?.Activity
  );
  const { widgetConfig, resType } = useSelector(
    (store) => store?.widgetSettings
  );

  const totalPrice =
    resType == "horeca" && data?.mainModuleType == "Horeca"
      ? adult * data?.fieldPrice1 +
          child * data?.fieldPrice2 +
          kid * data?.fieldPrice3 +
          baby * data?.fieldPrice4 +
          toddler * data?.fieldPrice5 || 0
      : data?.price *
        combinationQTY[data.idreservationtype + String(data.isBefore)];

  const originalDate = dayjs(selectedDate, "DD-MM-YYYY");

  // ── Translate day/month names using languageWords (same logic as YourBookingCard) ──
  const formatDateWithLanguage = (originalDate, languageWords) => {
    const formattedDate = originalDate.format("dddd, DD MMMM YYYY");
    const translatedDate = formattedDate
      .replace(
        /\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/g,
        (match) => languageWords?.[match] || languageWords?.[match.toLowerCase()] || match
      )
      .replace(
        /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g,
        (match) => languageWords?.[match] || match
      );
    return translatedDate;
  };

  const formattedDate = formatDateWithLanguage(originalDate, languageWords);

  const combinationUpsalePrice =
    combinationUpsale?.reduce((acc, item) => {
      return acc + item.quantity * item.price;
    }, 0) || 0;

  return (
    <Box sx={{ mb: 2, mt: 1, backgroundColor: cardCss?.CardSectionBgClr }}>
      <Accordion
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: cardCss?.CardSectionBgClr,
          "&:before": { display: "none" },
        }}
      >
        {/* ── SUMMARY ROW ── */}
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 2}}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {/* LEFT: Name */}
            <Typography
              sx={{ fontSize: "1rem", fontWeight: 500, flex: 1 }}
              color={cardCss?.CardSectionHeadingClr}
            >
              {data?.reservationType}
            </Typography>

            {/* CENTER: Time — nowrap prevents line break */}
            <Typography
              sx={{
                color: cardCss?.cardSectionLabelClr,
                fontSize: "13px",
              
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
                justifyContent: "center",
                px: 1,
              }}
            >
              {languageWords?.From}:{" "}
              <span style={{ color: cardCss?.CardSectionInfoClr }}>
                {data?.arrivalTime?.substring(0, 5)}
              </span>{" "}
            
              {languageWords?.To}:{" "}
              <span style={{ color: cardCss?.CardSectionInfoClr }}>
                {data?.depatureTime?.substring(0, 5)}
              </span>
            </Typography>

            {/* RIGHT: Price */}
            <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "500",
                  color: cardCss?.CardSectionPriceClr,
                }}
              >
                &euro;
                {format_Number(
                  parseFloat(totalPrice + combinationUpsalePrice).toFixed(2)
                )}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>

        <Divider />

        {/* ── DETAILS BODY ── */}
        <AccordionDetails sx={{ px: 2, py: 1.5 }}>
          {/* Date + guests row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left: date */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <AccessTimeIcon sx={{ color: cardCss?.CardSectionIconClr, fontSize: "20px" }} />
              <Box>
                {resType == "ticketing" ? (
                  <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                    {reservType?.isStandardTicket
                      ? dayjs(selectedDate).format("DD-MM-YYYY")
                      : selectedDate?.availableDate}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                    {formattedDate}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right: guest count */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1,mr:3 }}>
              <Box sx={{ width: "20px", height: "20px" }}>
                <GroupIcon
                  sx={{ color: cardCss?.CardSectionIconClr, width: "100%", height: "100%" }}
                />
              </Box>
              <Typography sx={{ fontSize: "13px", color: cardCss?.CardSectionInfoClr }}>
                {adult > 0 &&
                  `${
                    data.activitiesPriceType === "pricepertime" ||
                    !data.activitiesPriceType
                      ? adult
                      : totalCount
                  }`}
                {kid > 0 && `, ${kid}`}
                {child > 0 && `, ${child}`}
                {toddler > 0 && `, ${toddler}`}
                {baby > 0 && `, ${baby}`}
              </Typography>
            </Box>
          </Box>

          {/* ── Upsale products ── */}
          {combinationUpsale?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              {combinationUpsale?.map((item) => (
                <Box
                  key={item.idproduct}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <ProductionQuantityLimitsIcon
                      sx={{ color: cardCss?.CardSectionIconClr, fontSize: "20px" }}
                    />
                    <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                      {item?.quantity}x {item?.productName}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: cardCss?.CardSectionPriceClr,
                      minWidth: "60px",
                      textAlign: "right",
                      mr:3
                    }}
                  >
                    &euro;
                    {format_Number((item?.quantity * item?.price).toFixed(2))}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CombinationCard;

